import ApiCaller from '../redux/ApiCaller';
import { pictureToBlob, readAudioMetadata } from '../utils/AudioMetadataUtils';

const MAX_FALLBACK_SONG_SIZE = 1024 * 1024 * 1024;
const SONG_UPLOAD_CONCURRENCY = 2;

const isApiError = (response) => {
  return response?.error || response?.message || response?.status === false;
};

const assertApiResponse = (response, fallbackMessage) => {
  if (isApiError(response)) {
    throw new Error(response.error || response.message || fallbackMessage);
  }

  return response;
};

const getUploadSessionPayload = (session) => ({
  id: session.id,
  fileName: session.fileName,
  key: session.key,
  uploadId: session.uploadId,
});

const uploadFilePart = async ({ file, session, partNumber }) => {
  const start = (partNumber - 1) * session.partSize;
  const end = Math.min(start + session.partSize, file.size);
  const chunk = file.slice(start, end);

  const partUrlResponse = await ApiCaller('/v1/admin/songs/uploads/parts', 'POST', {
    ...getUploadSessionPayload(session),
    partNumber,
  });
  const partUrl = assertApiResponse(partUrlResponse, `Failed to prepare upload part ${partNumber}`);

  const uploadResponse = await fetch(partUrl.uploadUrl, {
    method: partUrl.method || 'PUT',
    body: chunk,
  });

  if (!uploadResponse.ok) {
    throw new Error(`Failed to upload part ${partNumber}`);
  }

  const etag = uploadResponse.headers.get('ETag') || uploadResponse.headers.get('etag');
  if (!etag) {
    throw new Error('Song part uploaded, but R2 did not expose the ETag header. Please check bucket CORS ExposeHeaders: ETag.');
  }

  return {
    part: {
      ETag: etag,
      PartNumber: partNumber,
    },
    uploadedBytes: end,
  };
};

const uploadArtwork = async (session, picture) => {
  if (!picture?.data?.length) return false;

  const imageBlob = pictureToBlob(picture);
  const imageUrlResponse = await ApiCaller(`/v1/admin/songs/uploads/${session.id}/image`, 'POST', {
    contentType: imageBlob.type || 'image/jpeg',
  });
  const imageUrl = assertApiResponse(imageUrlResponse, 'Failed to prepare song artwork upload');

  const uploadResponse = await fetch(imageUrl.uploadUrl, {
    method: imageUrl.method || 'PUT',
    headers: {
      'Content-Type': imageBlob.type || 'image/jpeg',
    },
    body: imageBlob,
  });

  if (!uploadResponse.ok) {
    throw new Error('Failed to upload song artwork');
  }

  return true;
};

const abortUpload = async (session) => {
  if (!session?.uploadId) return;

  await ApiCaller('/v1/admin/songs/uploads/abort', 'POST', getUploadSessionPayload(session)).catch(() => {});
};

export const uploadOneSong = async ({ file, bulkTime, fileIndex = 0, fileCount = 1, onProgress }) => {
  let session;

  try {
    if (file.size > MAX_FALLBACK_SONG_SIZE) {
      throw new Error(`${file.name} is larger than the ${Math.round(MAX_FALLBACK_SONG_SIZE / 1024 / 1024)}MB upload limit.`);
    }

    onProgress?.({ phase: 'metadata', file, fileIndex, fileCount, percent: 0 });
    const metadata = await readAudioMetadata(file).catch(() => ({}));

    onProgress?.({ phase: 'starting', file, fileIndex, fileCount, percent: 0 });
    const sessionResponse = await ApiCaller('/v1/admin/songs/uploads', 'POST', {
      originalName: file.name,
      mimeType: file.type || 'audio/mpeg',
      size: file.size,
    });
    session = assertApiResponse(sessionResponse, 'Failed to start song upload');

    const maxSize = session.maxSize || MAX_FALLBACK_SONG_SIZE;
    if (file.size > maxSize) {
      throw new Error(`${file.name} is larger than the ${Math.round(maxSize / 1024 / 1024)}MB upload limit.`);
    }

    const totalParts = Math.ceil(file.size / session.partSize);
    const parts = [];

    for (let partNumber = 1; partNumber <= totalParts; partNumber += 1) {
      const result = await uploadFilePart({ file, session, partNumber });
      parts.push(result.part);
      onProgress?.({
        phase: 'uploading',
        file,
        fileIndex,
        fileCount,
        partNumber,
        totalParts,
        percent: Math.round((result.uploadedBytes / file.size) * 100),
      });
    }

    onProgress?.({ phase: 'artwork', file, fileIndex, fileCount, percent: 100 });
    const hasImage = await uploadArtwork(session, metadata.picture);

    onProgress?.({ phase: 'finalizing', file, fileIndex, fileCount, percent: 100 });
    const completeResponse = await ApiCaller('/v1/admin/songs/uploads/complete', 'POST', {
      ...getUploadSessionPayload(session),
      originalName: file.name,
      title: metadata.title || null,
      artist: metadata.artist || null,
      album: metadata.album || null,
      hasImage,
      bulkTime,
      parts,
    });
    const completedSong = assertApiResponse(completeResponse, 'Failed to complete song upload');

    onProgress?.({ phase: 'complete', file, fileIndex, fileCount, percent: 100 });

    return completedSong;
  } catch (error) {
    await abortUpload(session);
    throw error;
  }
};

export const uploadSongs = async (files, { onProgress, bulkTime = Date.now(), concurrency = SONG_UPLOAD_CONCURRENCY } = {}) => {
  const songs = new Array(files.length);
  const workerCount = Math.min(Math.max(1, concurrency), files.length);
  let nextIndex = 0;
  let completedCount = 0;
  let firstError = null;

  const uploadSongAtIndex = async (index) => {
    try {
      songs[index] = await uploadOneSong({
        file: files[index],
        bulkTime,
        fileIndex: index,
        fileCount: files.length,
        onProgress: (progress) => {
          if (progress.phase === 'complete') {
            completedCount += 1;
          }
          onProgress?.({ ...progress, completedCount });
        },
      });
    } catch (error) {
      firstError = error;
    }
  };

  const runNextUpload = async () => {
    while (!firstError && nextIndex < files.length) {
      const index = nextIndex;
      nextIndex += 1;

      await uploadSongAtIndex(index);
    }
  };

  await Promise.all(Array.from({ length: workerCount }, runNextUpload));

  if (firstError) {
    throw firstError;
  }

  return songs;
};
